import React, { useContext, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Container, InventoryCheck } from '../../InventoryCheckView';
import {
  exportJsonAsCsv,
  exportJsonAsExcel,
  buildFullInventoryExportRows,
  fetchConsumerOriginMapsForChecks,
} from './inventoryExport';
import {
  Alert,
  Badge,
  PopoverBody,
  PopoverHeader,
  UncontrolledPopover,
} from 'reactstrap';
import ExportButton from '../../../components/Button/ExportButton';
import {
  FaAngleRight,
  FaCheckCircle,
  FaLightbulb,
  FaMinusCircle,
  FaQuestionCircle,
} from 'react-icons/fa';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { ContentSection } from '../../../components/Layout/ContentSection';
import {
  getInventoryCheckStatusColor,
  getInventoryStatusString,
} from './InventoryCheckTab';
import { ThemeContext } from '../../../providers/ThemeContextProvider';
import { Link } from 'react-router-dom';
import { Routes } from '@/utils/routes';
import { DateRangeFilter } from '@/components/FilterBar/DateRangeFilter';
import { type DateRange } from 'react-day-picker';

interface ContainerType {
  id: number;
  name: string;
}

const renderCell = (
  inventoryCheck: InventoryCheck,
  content: any,
  displayAsAnchor = false,
  asBadge = false,
  badgeColor = 'primary',
) => {
  if (asBadge) {
    return (
      <Badge color={badgeColor} pill>
        <span className="text-white">{content}</span>
      </Badge>
    );
  }

  if (displayAsAnchor) {
    return (
      <Link to={Routes.inventoryCheck(inventoryCheck.id)}>
        <span>{content}</span>
        <FaAngleRight />
      </Link>
    );
  }

  return (
    <div style={{ wordWrap: 'break-word', display: 'block' }}>
      <span>{content}</span>
    </div>
  );
};

type InventoryCheckTabInventoryProps = {
  vytalStoreGuid: string;
  title: string;
  isLoading: boolean;
  containerTypes: ContainerType[];
  inventoryChecks: InventoryCheck[];
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
};

const InventoryCheckTabInventory = ({
  vytalStoreGuid,
  title,
  isLoading,
  containerTypes,
  inventoryChecks,
  dateRange,
  onDateRangeChange,
}: InventoryCheckTabInventoryProps) => {
  const viewer = useContext(ViewerContext);
  const { theme } = useContext(ThemeContext);

  const [dataTableSelected, setDataTableSelected] = useState<InventoryCheck[]>(
    [],
  );

  const toJson = async (viewer: any) => {
    // expired and billable
    let containers: any[] = [];

    const inventoryChecksFiltered = dataTableSelected;

    const consumerMapsByInventoryId = await fetchConsumerOriginMapsForChecks(
      inventoryChecksFiltered,
    );

    for (const inventoryCheck of inventoryChecksFiltered) {
      containers = buildFullInventoryExportRows(
        viewer,
        [inventoryCheck],
        containerTypes,
        consumerMapsByInventoryId,
      );
    }

    return [...containers].sort(
      (a, b) =>
        a[i18n.t('InventoryCheck:typeId')] - b[i18n.t('InventoryCheck:typeId')],
    );
  };

  const exportCSV = async (viewer: any) => {
    const json = await toJson(viewer);

    if (json.length === 0) {
      LayoutStoreInstance.setError(i18n.t('InventoryCheck:exportNoContainers'));
      return;
    }
    exportJsonAsCsv(
      json,
      `inventory_information_${vytalStoreGuid}_${new Date().getTime()}.csv`,
    );
  };

  const exportExcel = async (viewer: any) => {
    const json = await toJson(viewer);
    exportJsonAsExcel(json, `inventory_information_${vytalStoreGuid}`);
  };

  const addIsRunningText = (
    inventoryCheck: InventoryCheck,
    text: string | number,
  ) => {
    const isRunning =
      (inventoryCheck.status === 1 ||
        inventoryCheck.status === 4 ||
        inventoryCheck.status === 5) &&
      inventoryCheck.isExpired === false &&
      inventoryCheck.isBillable === true;

    return `${text} ${
      isRunning ? i18n.t('InventoryCheck:isRunningIndicator') : ''
    }`;
  };

  const inventoryChecksWithDates = inventoryChecks.map((ic) => {
    return { ...ic, createdAt: new Date(ic.createdAt) };
  });

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <Alert color="warning">
        <div className="d-flex">
          <div className="align-self-center">
            <FaLightbulb />
          </div>
          <div className="ms-2">{i18n.t('InventoryCheck:helpText')}</div>
        </div>
      </Alert>

      <div
        className="d-flex align-items-center mb-3 gap-2"
        style={{ maxWidth: '350px' }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>
          {i18n.t('InventoryCheck:dateRange')}:
        </span>
        <DateRangeFilter
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder={i18n.t('InventoryCheck:selectDateRange')}
        />
      </div>

      <div className="mb-2 pt-3">
        <ExportButton
          disabled={dataTableSelected.length === 0}
          csvCallback={() => {
            exportCSV(viewer);
          }}
          excelCallback={() => {
            exportExcel(viewer);
          }}
        />
      </div>

      <DataTable
        value={inventoryChecksWithDates}
        dataKey="id"
        sortField="createdAt"
        sortOrder={-1}
        responsiveLayout="scroll"
        loading={isLoading}
        className="p-datatable-striped"
        paginator
        rows={10}
        totalRecords={inventoryChecksWithDates.length}
        selectionMode="checkbox"
        // @ts-ignore
        selection={dataTableSelected}
        selectionPageOnly={true}
        onSelectionChange={(e) => {
          // @ts-ignore
          setDataTableSelected(e.value);
        }}
      >
        <Column selectionMode="multiple" />

        {CanAccessPartnerData(viewer) && (
          <Column
            headerStyle={{ width: '200px' }}
            body={(i: InventoryCheck) => {
              return renderCell(i, i.type === 0 ? 'sync' : 'async', false);
            }}
            header={i18n.t('Common:type')}
            field="type"
          />
        )}

        <Column
          headerStyle={{ width: '200px' }}
          body={(i: InventoryCheck) => renderCell(i, i.id.slice(0, 13), true)}
          header={i18n.t('Common:id')}
          field="id"
        />

        <Column
          headerStyle={{ width: '200px' }}
          body={(i: InventoryCheck) =>
            renderCell(
              i,
              getInventoryStatusString(i),
              false,
              true,
              getInventoryCheckStatusColor(i),
            )
          }
          header={i18n.t('Common:status')}
          field="status"
        />

        <Column
          body={(i: InventoryCheck) => {
            return renderCell(
              i,
              i.isBillable ? (
                <FaCheckCircle
                  color={theme.color.success}
                  title={i18n.t('InventoryCheck:billable')}
                />
              ) : (
                <FaMinusCircle
                  color={theme.color.warning}
                  title={i18n.t('InventoryCheck:nonbillable')}
                />
              ),
            );
          }}
          header={i18n.t('InventoryCheck:billable')}
          field="isBillable"
        />

        <Column
          headerStyle={{ width: '100' }}
          body={(i: InventoryCheck) =>
            renderCell(
              i,
              new Date(i.createdAt).toLocaleDateString(
                i18n.language,
                getDateFormatOptions(false),
              ),
            )
          }
          header={i18n.t('InventoryCheck:createdAt')}
          field="createdAt"
          sortable
        />

        <Column
          headerStyle={{ width: '50' }}
          body={(i: InventoryCheck) => {
            return renderCell(i, addIsRunningText(i, i.total));
          }}
          header={
            <div>
              {i18n.t('InventoryCheck:total')}{' '}
              <FaQuestionCircle id="totalPopOverHover" />
              <UncontrolledPopover
                placement="top"
                target="totalPopOverHover"
                trigger="hover"
              >
                <PopoverHeader>
                  {i18n.t('InventoryCheck:totalPopOverTitle')}
                </PopoverHeader>

                <PopoverBody className="keep-line-breaks">
                  {i18n.t('InventoryCheck:totalPopOverText')}
                </PopoverBody>
              </UncontrolledPopover>
            </div>
          }
          field="total"
        />

        <Column
          headerStyle={{ width: '50px' }}
          body={(i: InventoryCheck) => {
            return renderCell(
              i,
              addIsRunningText(
                i,
                `${i.recoveredCount + i.confirmedContainers.length}`,
              ),
            );
          }}
          header={i18n.t('InventoryCheck:recoveredContainers')}
          field="recoveredCount"
        />

        <Column
          headerStyle={{ width: '50' }}
          body={(i: InventoryCheck) => {
            return renderCell(i, addIsRunningText(i, i.missingCount));
          }}
          header={
            <div>
              {i18n.t('InventoryCheck:missingContainers')}{' '}
              <FaQuestionCircle id="missingPopOverHover" />
              <UncontrolledPopover
                placement="top"
                target="missingPopOverHover"
                trigger="hover"
              >
                <PopoverHeader>
                  {i18n.t('InventoryCheck:missingPopOverTitle')}
                </PopoverHeader>

                <PopoverBody className="keep-line-breaks">
                  {i18n.t('InventoryCheck:missingPopOverText')}
                </PopoverBody>
              </UncontrolledPopover>
            </div>
          }
          field="missingCount"
        />

        <Column
          headerStyle={{ width: '50' }}
          body={(i: InventoryCheck) => {
            return renderCell(i, i.brokenCount || '-');
          }}
          header={<div>{i18n.t('InventoryCheck:brokenContainers')}</div>}
          field="brokenCount"
        />
      </DataTable>
    </ContentSection>
  );
};

export default InventoryCheckTabInventory;
